import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import moment from "moment-timezone";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { connect } from "react-redux";
import { CALL_FEATCH_API, CALL_OUTLOOK_API } from "common/API";
import { getUserInfo, getUserList, getClientid } from "redux/selectors";
import { CalendarContext } from ".";
import styled, { keyframes } from "styled-components";
import { Checkbox, Button } from "antd";
import LeaveForm from "organisms/LeaveForm";
import CreateEventModal from "organisms/CreateEventModal";
import StaffLeaveIndicatorInCalendar from "./StaffLeaveIndicatorInCalendar";
import { ReactComponent as Editicon } from "../../assets/Edit_Pencil_01.svg";
import { ReactComponent as TrashDelete } from "../../assets/Trash_Full.svg";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";


// ─── Layout ───────────────────────────────────────────────────────────────────

const PageWrap = styled.div`
  display: flex;
  flex-direction: column;
`;

// ─── Shared icon button style (matches Myleavereport.js EDIT_ICON_STYLE) ─────
const ICON_BTN_STYLE = {
  color: "#00685F",
  cursor: "pointer",
  background: "#deefee",
  padding: "2px",
  borderRadius: "4px",
  height: "25px",
  width: "25px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 18,
};

const TopBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
`;

const CalTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: #1f243b;
  margin: 0;
`;


const NavBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
  padding-bottom: 14px;
  border-bottom: 2px solid #d9d9d9;
`;

const DateNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DateRangeText = styled.span`
  font-size: 24px;
  font-weight: 700;
  color: #1b1b1d;
  letter-spacing: -0.64px;
`;

const NavBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: #00685f;
  padding: 0 2px;
  font-size: 22px;
  font-weight: 600;
  line-height: 1;
  display: flex;
  align-items: center;
  &:hover { opacity: 0.65; }
`;

const ViewGroup = styled.div`
  display: flex;
  border: 1px solid #e2e2e8;
  border-radius: 4px;
  overflow: hidden;
`;

const ViewBtn = styled.button`
  padding: 6px 22px;
  border: none;
  border-right: ${({ $notlast }) => ($notlast ? "1px solid #e2e2e8" : "none")};
  background: ${({ $active }) => ($active ? "#636980" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#636980")};
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  cursor: pointer;
  transition: background 0.15s;
  &:hover { background: ${({ $active }) => ($active ? "#636980" : "#f5f5f5")}; }
`;

const MainContent = styled.div`
  display: flex;
  gap: 24px;
  align-items: flex-start;
`;

const Sidebar = styled.div`
  width: 220px;
  flex-shrink: 0;
`;

const MiniCalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  height: 40px;
  margin-bottom: 4px;
  background: #f6f6f6;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  color: #1f243b;
`;

const MiniNavGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MiniNavBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { opacity: 0.65; }
`;

const FilterSection = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CalArea = styled.div`
  flex: 1;
  min-width: 0;
`;

const CalendarContainer = styled.div`
  height: calc(100vh - 240px);
  min-height: 500px;
  overflow: hidden;
`;

// ─── Mini month calendar (pure, no Ant) ────────────────────────────────────────

const MiniGridBox = styled.div`
  background: #f6f6f6;
  border-radius: 8px;
  padding: 10px 12px 14px;
`;

const MiniGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  row-gap: 4px;
`;

const MiniWeekRow = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  padding-bottom: 4px;
  margin-bottom: 6px;
  border-bottom: 2px solid #ffffff;
`;

const MiniWeekday = styled.div`
  text-align: center;
  font-size: 12px;
  font-weight: 400;
  color: #636980;
`;

const MiniDay = styled.button`
  height: 26px;
  width: 26px;
  border: none;
  background: ${({ $today }) => ($today ? "#1f243b" : "transparent")};
  color: ${({ $today, $muted }) =>
    $today ? "#fff" : $muted ? "#d9d9d9" : "#1f243b"};
  font-weight: ${({ $today }) => ($today ? 600 : 400)};
  font-size: 13px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  margin: 0 auto;
  transition: background 0.12s;
  &:hover {
    background: ${({ $today }) => ($today ? "#1f243b" : "#ececec")};
  }
`;

function MiniMonth({ date, onPick }) {
  const base = date ? moment(date) : moment();
  const monthIdx = base.month();
  const gridStart = base.clone().startOf("month").startOf("week");
  const today = moment().format("YYYY-MM-DD");
  const cells = Array.from({ length: 42 }, (_, i) => gridStart.clone().add(i, "days"));

  return (
    <MiniGridBox>
      <MiniWeekRow>
        {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
          <MiniWeekday key={d}>{d}</MiniWeekday>
        ))}
      </MiniWeekRow>
      <MiniGrid>
        {cells.map((c) => {
          const key = c.format("YYYY-MM-DD");
          return (
            <MiniDay
              key={key}
              $muted={c.month() !== monthIdx}
              $today={key === today}
              onClick={() => onPick?.(c)}
            >
              {c.date()}
            </MiniDay>
          );
        })}
      </MiniGrid>
    </MiniGridBox>
  );
}

// ─── Custom Modal (portal, no Ant) ─────────────────────────────────────────────

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 1000;
  padding: 90px 16px 16px;
`;

const ModalCard = styled.div`
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.2);
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 120px);
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ModalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  font-size: 22px;
  line-height: 1;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  &:hover { color: #333; }
`;

const ModalBody = styled.div`
  padding: 16px 20px 20px;
  overflow-y: auto;
`;

function Modal({ open, onClose, title, width = 480, children }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <Overlay onMouseDown={onClose}>
      <ModalCard style={{ width }} onMouseDown={(e) => e.stopPropagation()}>
        <ModalHead>
          {title}
          <CloseBtn onClick={onClose}>&times;</CloseBtn>
        </ModalHead>
        <ModalBody>{children}</ModalBody>
      </ModalCard>
    </Overlay>,
    document.body
  );
}

// ─── Custom Spinner (no Ant) ───────────────────────────────────────────────────

const spin = keyframes`to { transform: rotate(360deg); }`;

const Spinner = styled.div`
  width: ${({ $large }) => ($large ? 34 : 22)}px;
  height: ${({ $large }) => ($large ? 34 : 22)}px;
  border: 3px solid #e6e6e6;
  border-top-color: #00685f;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
`;

const SpinContainer = styled.div`
  position: relative;
`;

const SpinMask = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
`;

function Spin({ spinning, size, children }) {
  if (children !== undefined) {
    return (
      <SpinContainer>
        {children}
        {spinning && (
          <SpinMask>
            <Spinner />
          </SpinMask>
        )}
      </SpinContainer>
    );
  }
  return <Spinner $large={size === "large"} />;
}

// ─── Custom toast (replaces antd message) ──────────────────────────────────────

const ToastWrap = styled.div`
  position: fixed;
  top: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 2000;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ToastItem = styled.div`
  background: #fff;
  border: 1px solid #ffccc7;
  color: #cf1322;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  padding: 10px 16px;
  font-size: 13.5px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

// ─── Sync icon (replaces antd SyncOutlined) ────────────────────────────────────

const SyncIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="23 4 23 10 17 10" />
    <polyline points="1 20 1 14 7 14" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

// ─── Event detail modal styled components ─────────────────────────────────────

const EvDetailLabel = styled.span`
  width: 160px;
  flex-shrink: 0;
  color: #888;
  font-size: 13px;
  padding-top: 1px;
`;

const EvDetailValue = styled.span`
  color: #111;
  font-size: 13.5px;
  font-weight: 450;
  line-height: 1.65;
  a { color: #1890ff; text-decoration: none; &:hover { text-decoration: underline; } }
`;

const EvDetailRow = styled.div`
  display: flex;
  align-items: flex-start;
  padding: 10px 0;
  &.section-title { padding-top: 18px; border-bottom: 1px solid #e0e0e0; }
`;

const DeleteBtn = styled(Button)`
  && {
    height: 38px; padding: 0 28px; border-radius: 10px;
    background: #b2f0e3; border: none; color: #111827; font-weight: 600; box-shadow: none;
    &:hover, &:focus { background: #7de8d1 !important; color: #111827 !important; }
  }
`;

const CancelConfirmBtn = styled(Button)`
  && {
    height: 38px; padding: 0 24px; border-radius: 10px;
    border: 1px solid #a6a7aa; color: #374151; font-weight: 500; box-shadow: none;
    &:hover { border-color: #9ca3af; color: #111827; }
  }
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-checked .ant-checkbox-inner { background-color: #00DA88 !important; border-color: #00DA88 !important; }
  .ant-checkbox-checked::after { border-color: #00DA88 !important; }
  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-input:focus + .ant-checkbox-inner { border-color: #00DA88 !important; }
  .ant-checkbox-inner::after { border-color: #111 !important; }
`;

// ─── Chevron arrows (from Insyts design assets) ────────────────────────────────

const ChevronLeft = ({ size = 9 }) => (
  <svg width={size} height={(size * 16) / 9} viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 15L1 8L8 1" stroke="#00685F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ChevronRight = ({ size = 9 }) => (
  <svg width={size} height={(size * 16) / 9} viewBox="0 0 9 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 15L8 8L1 1" stroke="#00685F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// ─── FullCalendar CSS overrides ────────────────────────────────────────────────

const FcWrap = styled.div`
  .fc .fc-day-today { background: rgba(0, 104, 95, 0.04) !important; }
  /* ── Now indicator: thin red line spanning the whole week + dot at axis ── */
  .fc .fc-timegrid-col,
  .fc .fc-timegrid-col-frame,
  .fc .fc-timegrid-cols,
  .fc .fc-timegrid-body,
  .fc .fc-timegrid-slots,
  .fc .fc-view-harness,
  .fc .fc-timegrid,
  .fc .fc-scrollgrid,
  .fc .fc-scrollgrid-section,
  .fc .fc-scrollgrid-section > td,
  .fc .fc-timegrid-now-indicator-container {
    overflow: visible !important;
  }
  /* lift today's column so its overflowing line paints above sibling days */
  .fc .fc-timegrid-col.fc-day-today { position: relative; z-index: 6; }

  /* ── Week view: line spans across all day columns ── */
  .fc-timeGridWeek-view .fc-timegrid-now-indicator-line {
    border-width: 2px 0 0;
    border-color: #ba1a1a !important;
    z-index: 10 !important;
    left: -9999px !important;
    right: auto !important;
    width: 19998px !important;
  }

  /* ── Day view: line spans the single column normally ── */
  .fc-timeGridDay-view .fc-timegrid-now-indicator-line {
    border-width: 2px 0 0;
    border-color: #ba1a1a !important;
    z-index: 10 !important;
    left: 0 !important;
    right: 0 !important;
    width: auto !important;
  }
  .fc-timeGridDay-view .fc-timegrid-col,
  .fc-timeGridDay-view .fc-timegrid-col-frame,
  .fc-timeGridDay-view .fc-timegrid-now-indicator-container {
    overflow: visible !important;
  }

  /* dot's positioning context is fc-timegrid-axis-frame (right edge = start of day columns) */
  .fc .fc-timegrid-axis,
  .fc .fc-timegrid-axis-frame { overflow: visible !important; }
  .fc .fc-timegrid-now-indicator-arrow {
    border: none !important;
    width: 12px !important;
    height: 12px !important;
    border-radius: 50% !important;
    background: #ba1a1a !important;
    margin-top: -6px !important;
    left: auto !important;
    right: -6px !important;
    z-index: 11 !important;
  }
  .fc-more-popover { display: none !important; }
  .fc-timegrid-event.planner-event-top { z-index: 10 !important; }
  .fc-timegrid-allday-cushion { padding-top: 2px !important; padding-bottom: 2px !important; }

  /* mask the part of the now-indicator line that bleeds left into the time-label gutter */
  .fc .fc-timegrid-slot-label {
    position: relative !important;
    z-index: 12 !important;
    background: #fff !important;
  }

  /* ── Container: card stroke + rounded corners (Frame 2147229110) ── */
  .fc {
    --fc-border-color: #D9D9D9;
    --fc-now-indicator-color: #BA1A1A;
    --fc-today-bg-color: transparent;
    border: 1px solid #e2e2e8;
    border-radius: 0;
    overflow: hidden;
    font-family: 'DM Sans', sans-serif;
  }
  .fc .fc-scrollgrid { border-radius: 0; border: none; }
  .fc .fc-scrollgrid-section > * { border-right: none; }
  .fc table { border-top: none; }

  /* ── Hide day/date column header in day view only ── */
  .fc-timeGridDay-view .fc-scrollgrid-section-header { display: none !important; }

  /* ── Header row (Frame 2147229100): #FCF8FA, 71px ── */
  .fc .fc-col-header { background: #fcf8fa; }
  .fc .fc-col-header-cell { height: 71px; vertical-align: middle; border-color: #d9d9d9; }
  .fc .fc-col-header-cell-cushion {
    width: 100%;
    padding: 14px 4px;
    text-decoration: none !important;
  }
  /* the timegrid axis (top-left empty corner) also part of header bg */
  .fc .fc-timegrid-axis { background: #fcf8fa; border-color: #d9d9d9; }

  /* ── All Day row (Frame 2147229102): #F0EDEF ── */
  .fc .fc-timegrid-divider { display: none; }
  .fc .fc-daygrid-body, .fc .fc-timegrid-axis-frame { }
  .fc .fc-scrollgrid-section-header + .fc-scrollgrid-section .fc-timegrid-axis { background: #f0edef; }
  .fc .fc-scrollgrid-section-liquid:first-child .fc-scroller-harness,
  .fc .fc-scrollgrid-section-liquid:first-child .fc-scroller { background: #f0edef !important; }
  .fc .fc-timegrid-allday .fc-timegrid-axis-cushion { color: #636980; font-size: 16px; font-weight: 500; }

  /* ── Time slot rows ── */
  .fc .fc-timegrid-slot { height: 44px; border-color: #d9d9d9; }
  .fc .fc-timegrid-slot-minor { border-top-style: none; }
  .fc .fc-timegrid-slot-label { vertical-align: top; }
  .fc .fc-timegrid-slot-label-cushion { font-size: 14px; color: #636980; padding: 2px 8px 0; }
  .fc .fc-timegrid-axis-cushion { font-size: 14px; color: #636980; }

  /* ── Vertical day column separators ── */
  .fc .fc-timegrid-col, .fc .fc-daygrid-day { border-color: #d9d9d9; }

  .fc .fc-timegrid-allday { background: #f0edef !important; }
  .fc .fc-timegrid-allday td,
  .fc .fc-timegrid-allday .fc-daygrid-day,
  .fc .fc-timegrid-allday .fc-daygrid-day-bg,
  .fc .fc-daygrid-body,
  .fc .fc-daygrid-body td,
  .fc .fc-daygrid-body .fc-daygrid-day,
  .fc .fc-daygrid-body .fc-day,
  .fc .fc-daygrid-body .fc-day-today,
  .fc .fc-daygrid-body tr,
  .fc .fc-daygrid-body .fc-scroller { background: #f0edef !important; }
  .fc .fc-timegrid-allday .fc-daygrid-day-frame {
    height: 50px !important; min-height: 50px !important; overflow: hidden !important;
  }
  .fc .fc-timegrid-allday .fc-daygrid-day-events {
    display: flex !important; flex-direction: column !important; height: 100% !important;
    gap: 1px !important; margin: 0 !important; padding: 0 !important;
    position: absolute !important; top: 0 !important; left: 0 !important;
    right: 0 !important; bottom: 0 !important;
  }
  .fc .fc-timegrid-allday .fc-daygrid-event-harness {
    flex: 1 1 0 !important; min-height: 0 !important;
    position: relative !important; top: 0 !important; inset: unset !important;
  }
  .fc .fc-timegrid-allday .fc-daygrid-event {
    height: 100% !important; min-height: 0 !important; margin: 0 !important;
    position: relative !important; top: 0 !important;
    background: transparent !important; border: none !important;
  }
  .fc .fc-timegrid-allday .fc-event-main { padding: 0 !important; height: 100% !important; }
  .fc .fc-timegrid-allday .fc-daygrid-more-link { display: none !important; }
  .fc .fc-daygrid-more-link { background: transparent !important; }
  .fc .fc-timegrid-more-link { background: transparent !important; }
  .fc .fc-daygrid-event-harness-abs { background: transparent !important; }
  .fc .fc-more-link { background: transparent !important; box-shadow: none !important; }
  /* ── Kill any inner scrollbars on event blocks ── */
  .fc-event,
  .fc-event-main,
  .fc-event-main-frame,
  .fc-daygrid-event,
  .fc-timegrid-event,
  .fc-daygrid-event-harness,
  .fc-timegrid-event-harness {
    overflow: hidden !important;
  }
  .fc-event *,
  .fc-daygrid-event *,
  .fc-timegrid-event * {
    overflow: hidden !important;
    scrollbar-width: none !important;      /* Firefox */
    -ms-overflow-style: none !important;   /* old Edge/IE */
  }
  .fc-event *::-webkit-scrollbar,
  .fc-daygrid-event *::-webkit-scrollbar,
  .fc-timegrid-event *::-webkit-scrollbar {
    display: none !important;              /* Chrome/Safari */
  }
`;

// ─── Detail row for modals ─────────────────────────────────────────────────────

function DetailRow({ label, value, isLink }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", padding: "9px 0", borderBottom: "1px solid #f5f5f5" }}>
      <span style={{ width: 150, flexShrink: 0, color: "#888", fontSize: 13 }}>{label}</span>
      <span style={{ color: "#111", fontSize: 13.5 }}>
        {isLink ? <a href={value} target="_blank" rel="noreferrer">{value}</a> : value}
      </span>
    </div>
  );
}

function formatDateTime(dt) {
  if (!dt) return "---";
  const m = moment.utc(dt);
  return m.isValid() ? m.format("Do MMM YYYY; hh:mm A") : "---";
}

function formatHours(hours) {
  const num = Number(hours).toFixed(2);
  const unit = num <= 1 ? "Hr" : "Hrs";
  return `${num} ${unit}/day`;
}

function hexToRgba(hex, alpha = 1) {
  const h = (hex || "#EB4747").replace("#", "");
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const bigint = parseInt(full, 16);
  if (Number.isNaN(bigint)) return `rgba(235, 71, 71, ${alpha})`;
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function buildEditData(evt, updatedBy = "") {
  return {
    eventid: evt.eventid,
    eventname: evt.eventname || "",
    matterid: evt.matterid || "",
    mattername: evt.mattername || "",
    startdatetime: moment.utc(evt.startdatetime || evt.start_datetime).format("YYYY-MM-DD HH:mm"),
    enddatetime: moment.utc(evt.enddatetime || evt.end_datetime).format("YYYY-MM-DD HH:mm"),
    location: evt.location || "",
    meetinglink: evt.meetinglink || "",
    attendees: Array.isArray(evt.attendees) ? evt.attendees : [],
    clients: Array.isArray(evt.clients) ? evt.clients : [],
    responsiblepersons: Array.isArray(evt.responsiblepersons) ? evt.responsiblepersons : [],
    description: evt.description || "",
    sendremaindercount: evt.sendremindercount || evt.sendremaindercount || "",
    sendremaindertype: evt.sendremindertype || evt.sendremaindertype || "",
    calendarstatus: evt.allcalendarstatus === true,
    updatedby: updatedBy,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

function MyPlannerCalendar({ userInfo, ClientId, userList }) {
  const { tabBarExtraContent, miniCalendarCurrentDate, previousMonth, nextMonth, eventMinePermission } =
    React.useContext(CalendarContext);

  const calendarRef = useRef(null);

  const currentUser = userList?.find((item) => item?.userid === userInfo?.userid);
  const baselocationid = currentUser?.baselocationid;

  // FullCalendar view state (driven by FullCalendar itself via datesSet)
  const [viewTitle, setViewTitle] = useState("");
  const [currentView, setCurrentView] = useState("timeGridWeek");

  const [dateRange, setDateRange] = useState({
    start: moment().startOf("week").format("YYYY-MM-DD"),
    end: moment().endOf("week").format("YYYY-MM-DD"),
  });

  const [show, setShow] = useState({ event: true, leave: true, matter: true, outlook: true, holiday: true });
  const toggle = (k) => setShow((p) => ({ ...p, [k]: !p[k] }));

  const [plannerData, setPlannerData] = useState({ events: [], leaves: [], engagements: [], holidays: [] });
  const [plannerLoading, setPlannerLoading] = useState(false);

  const [outlookConnected, setOutlookConnected] = useState(localStorage.getItem("OutlookStatus") === "true");
  const [showOutlookModal, setShowOutlookModal] = useState(false);
  const [selectedOutlookData, setSelectedOutlookData] = useState(null);
  const [outlookEvents, setOutlookEvents] = useState([]);

  const [modal, setModal] = useState({ open: false, type: null, data: null, loading: false });
  const closeModal = () => setModal({ open: false, type: null, data: null, loading: false });

  const [leaveForm, setLeaveForm] = useState({ open: false, data: {} });
  const [editVisible, setEditVisible] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [sendCancellationEmail, setSendCancellationEmail] = useState(false);
  const deleteEventIdRef = useRef(null);

  const resolveNames = useCallback((emailsOrIds) => {
    if (!Array.isArray(emailsOrIds) || !emailsOrIds.length) return "---";
    return emailsOrIds
      .map((val) => {
        const user = userList?.find(
          (u) => u?.email === val || u?.userid === val || u?.emailid === val
        );
        return user?.fullname || user?.firstname || val;
      })
      .join(", ");
  }, [userList]);

  const [allBookings, setAllBookings] = useState({ open: false, date: null, items: [] });
  const closeAllBookings = () => setAllBookings({ open: false, date: null, items: [] });

  // ── Toasts (replaces antd message) ──────────────────────────────────────────
  const [toasts, setToasts] = useState([]);
  const showError = useCallback((msg) => {
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, msg }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 3000);
  }, []);

  const handleEditClick = useCallback(() => {
    closeModal();
    setEditVisible(true);
  }, []);

  const handleDeleteClick = useCallback(() => {
    setSendCancellationEmail(false);
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    const eventId = deleteEventIdRef.current || modal.data?.eventid;
    if (!eventId) return;
    try {
      setDeleteLoading(true);
      const payload = {
        eventid: String(eventId),
        deletedby: userInfo?.userid || "",
        deletedbyname: userInfo?.name || userInfo?.firstname || "",
        sendcancellationemail: sendCancellationEmail,
      };
      const response = await CALL_FEATCH_API("insyts-calendar/deleteevents", "delete", payload);
      if (response?.status === "SUCCESS" || response?.status === 200 || response?.statusCode === 200) {
        deleteEventIdRef.current = null;
        setSendCancellationEmail(false);
        setDeleteConfirmOpen(false);
        closeModal();
        fetchPlannerData();
      } else {
        showError(response?.message || "Failed to delete event");
      }
    } catch {
      showError("Something went wrong while deleting");
    } finally {
      setDeleteLoading(false);
    }
  }, [modal.data?.eventid, userInfo, sendCancellationEmail, showError]);

  // ── Custom toolbar handlers ────────────────────────────────────────────────
  const handlePrev = () => calendarRef.current?.getApi().prev();
  const handleNext = () => calendarRef.current?.getApi().next();
  const handleViewChange = (view) => {
    calendarRef.current?.getApi().changeView(view);
    setCurrentView(view);
  };
  const handleMiniPick = (d) => calendarRef.current?.getApi().gotoDate(d.toDate());

  // ── Planner API ───────────────────────────────────────────────────────────
  const fetchPlannerData = useCallback(async () => {
    if (!userInfo?.userid || !dateRange.start || !dateRange.end) return;
    setPlannerLoading(true);
    try {
      const res = await CALL_FEATCH_API("insyts/my-planner", "post", {
        userid: userInfo.userid,
        start_date: dateRange.start,
        end_date: dateRange.end,
      });
      const data = res?.response ?? res ?? {};
      setPlannerData({
        events: data.events ?? [],
        leaves: data.leaves ?? [],
        engagements: data.engagements ?? [],
        holidays: data.holidays ?? [],
      });
    } catch (e) {
      console.error("[MyPlanner] fetch error", e);
    } finally {
      setPlannerLoading(false);
    }
  }, [userInfo?.userid, dateRange.start, dateRange.end]);

  useEffect(() => { fetchPlannerData(); }, [fetchPlannerData]);

  // ── OAuth return detection ────────────────────────────────────────────────
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("outlook_connected") === "true") {
      localStorage.setItem("OutlookStatus", "true");
      setOutlookConnected(true);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  // ── Outlook ───────────────────────────────────────────────────────────────
  const fetchOutlookEvents = useCallback(async (start, end) => {
    try {
      const res = await CALL_OUTLOOK_API("insyts/outlook/calendar", {
        startDateTime: `${moment(start).format("YYYY-MM-DD")}T00:00:00Z`,
        endDateTime: `${moment(end).format("YYYY-MM-DD")}T23:59:59Z`,
      });
      setOutlookEvents(buildOutlookItems(res?.response?.data ?? []));
    } catch {
      setOutlookEvents([]);
    }
  }, []);

  const handleConnectOutlook = useCallback(async () => {
    try {
      const returnUrl = `${window.location.origin}/calendar?outlook_connected=true`;
      const res = await CALL_OUTLOOK_API("insyts/outlook/login", { returnUrl });
      const url = res?.response?.redirectUrl;
      if (url) window.location.href = url;
      else showError("Could not initiate Outlook login. Please try again.");
    } catch {
      showError("Failed to connect to Outlook.");
    }
  }, [showError]);

  useEffect(() => {
    if (outlookConnected && dateRange.start && dateRange.end) {
      fetchOutlookEvents(dateRange.start, dateRange.end);
    } else {
      setOutlookEvents([]);
    }
  }, [outlookConnected, dateRange.start, dateRange.end, fetchOutlookEvents]);

  // ── Event builders ────────────────────────────────────────────────────────
  const rawEvents = plannerData.events;
  const myLeaves = plannerData.leaves;
  const myMatterResources = plannerData.engagements;

  const eventFcItems = useMemo(() => {
    if (!show.event) return [];
    return rawEvents.map((evt) => ({
      id: `event-${evt.eventid}`,
      title: evt.eventname || "Event",
      start: moment.utc(evt.startdatetime || evt.start_datetime).format("YYYY-MM-DDTHH:mm:ss"),
      end: moment.utc(evt.enddatetime || evt.end_datetime).format("YYYY-MM-DDTHH:mm:ss"),
      backgroundColor: "#FFEDD4",
      borderColor: "#F59E0B",
      textColor: "#1F243B",
      order: 0,
      classNames: ["planner-event-top"],
      extendedProps: { _type: "event", _id: evt.eventid, _raw: evt },
    }));
  }, [rawEvents, show.event]);

  const leaveFcItems = useMemo(() => {
    if (!show.leave) return [];
    return myLeaves.map((ev) => {
      const isCancelReq = ev.status_id === "5" && ev.having_cancel_request === "1";
      const rawStart = isCancelReq ? ev.new_start_date : ev.start_date;
      const rawEnd = isCancelReq ? ev.new_end_date : ev.end_date;
      const isApproved = ev.status_id === "5" && !isCancelReq;
      return {
        id: `leave-${ev.user_leave_id || ev.id || rawStart}`,
        title: ev.leave_category_name || "Leave",
        start: moment(rawStart).format("YYYY-MM-DD"),
        end: moment(rawEnd).clone().add(1, "day").format("YYYY-MM-DD"),
        allDay: true,
        backgroundColor: "#FFDAD6",
        borderColor: "#E11D48",
        textColor: "#93000A",
        extendedProps: { _type: "leave", _raw: ev, _approved: isApproved },
      };
    });
  }, [myLeaves, show.leave]);

  const matterFcItems = useMemo(() => {
    if (!show.matter) return [];
    const bookedDates = myMatterResources.reduce((acc, res) => {
      const start = moment(res.actualBookedStartDate, "YYYY-MM-DD");
      const end = moment(res.actualBookedEndDate, "YYYY-MM-DD");
      if (!start.isValid() || !end.isValid()) {
        acc.push({ date: moment().format("YYYY-MM-DD"), ...res });
        return acc;
      }
      let cur = start.clone();
      while (cur.isBefore(end) || cur.isSame(end, "day")) {
        if (cur.day() !== 0 && cur.day() !== 6) {
          acc.push({ date: cur.format("YYYY-MM-DD"), ...res });
        }
        cur.add(1, "day");
      }
      return acc;
    }, []);

    const byDate = {};
    bookedDates.forEach((e) => {
      if (!byDate[e.date]) byDate[e.date] = [];
      byDate[e.date].push(e);
    });

    const calcDaily = (res) => {
      const personDay = parseFloat(res.personDay) || 0;
      const workingDays = parseInt(res.bookedWorkingDays) || 0;
      if (personDay <= 0 || workingDays <= 0) return 8;
      return Math.min((personDay * 8) / workingDays, 8);
    };

    const out = [];
    Object.entries(byDate).forEach(([date, entries]) => {
      entries.forEach((e, i) => {
        const dh = calcDaily(e);
        const bsStr = moment(date).set({ hour: 8, minute: 0, second: 0 }).toISOString();
        const beStr = moment(date).set({ hour: 8, minute: 0, second: 0 }).add(dh, "hours").toISOString();
        out.push({
          id: `matter-${date}-${e.resourceId || ""}-${i}`,
          title: e.matterName || "Matter",
          start: bsStr,
          end: beStr,
          backgroundColor: "#DCFCE7",
          borderColor: "#10B981",
          textColor: "#1F243B",
          order: 2,
          extendedProps: { _type: "matter", _raw: e, _dailyHours: dh },
        });
      });
    });
    return out;
  }, [myMatterResources, show.matter]);

  const myHolidays = plannerData.holidays;

  const holidayFcItems = useMemo(() => {
    if (!show.holiday) return [];
    return myHolidays
      .filter((h) => !baselocationid || h.location_id?.includes(baselocationid))
      .map((h) => {
        const start = moment(h.start_date);
        const end = moment(h.end_date).isValid() && moment(h.end_date).isSameOrAfter(start, "day")
          ? moment(h.end_date)
          : start;
        return {
          id: `holiday-${h.holiday_id}`,
          title: h.holiday_name || "Holiday",
          start: start.format("YYYY-MM-DD"),
          end: end.clone().add(1, "day").format("YYYY-MM-DD"),
          allDay: true,
          backgroundColor: hexToRgba("#ff0000", 0.18),
          borderColor: "#ff0000",
          textColor: "#1f243b",
          extendedProps: { _type: "holiday", _raw: h },
        };
      });
  }, [myHolidays, show.holiday, baselocationid]);

  const fcEvents = useMemo(
    () => [...eventFcItems, ...leaveFcItems, ...matterFcItems, ...holidayFcItems, ...(show.outlook ? outlookEvents : [])]
      .filter((ev) => moment(ev.start).day() !== 0),
    [eventFcItems, leaveFcItems, matterFcItems, holidayFcItems, outlookEvents, show.outlook]
  );

  // ── datesSet — captures FullCalendar's title + view type ─────────────────
  const handleDatesSet = useCallback((arg) => {
    setViewTitle(
      arg.view.type === "timeGridDay"
        ? moment(arg.start).format("dddd, MMMM D, YYYY")
        : arg.view.title
    );
    setCurrentView(arg.view.type);
    const start = moment(arg.start).format("YYYY-MM-DD");
    const end = moment(arg.end).format("YYYY-MM-DD");
    setDateRange((prev) =>
      prev.start === start && prev.end === end ? prev : { start, end }
    );
  }, []);

  // ── Event click ───────────────────────────────────────────────────────────
  const handleEventClick = useCallback(async (info) => {
    const { _type, _raw, _id } = info.event.extendedProps;

    if (_type === "leave") {
      // Leaves show their details via a hover tooltip (same as Leave
      // calendar > Mine), so a click should not open the leave form.
      return;
    }
    if (_type === "matter") {
      setModal({ open: true, type: _type, data: _raw, loading: false });
      return;
    }
    if (_type === "holiday") {
      setModal({ open: true, type: "holiday", data: _raw, loading: false });
      return;
    }
    if (_type === "outlook") {
      setSelectedOutlookData(info.event.extendedProps);
      setShowOutlookModal(true);
      return;
    }
    if (_type === "event") {
      deleteEventIdRef.current = _id;
      setModal({ open: true, type: "event", data: null, loading: true });
      try {
        const res = await CALL_FEATCH_API("insyts-calendar/getallevents", "post", { eventid: _id });
        const raw = res?.response?.data ?? res?.data;
        setModal({ open: true, type: "event", data: Array.isArray(raw) ? raw[0] : raw, loading: false });
      } catch {
        showError("Failed to load event details");
        closeModal();
      }
    }
  }, [showError]);

  // ── More-link click ───────────────────────────────────────────────────────
  const handleMoreLinkClick = useCallback((info) => {
    info.jsEvent.preventDefault();
    info.jsEvent.stopPropagation();
    info.jsEvent.stopImmediatePropagation();
    const dateStr = moment(info.date).format("YYYY-MM-DD");
    const dayItems = fcEvents.filter((ev) =>
      moment(ev.start).format("YYYY-MM-DD") === dateStr
    );
    setAllBookings({
      open: true,
      date: moment(info.date).format("dddd, MMM D YYYY"),
      items: dayItems,
    });
    return "stop";
  }, [fcEvents]);

  // ── Mini-calendar month display ───────────────────────────────────────────
  const miniMonth = miniCalendarCurrentDate
    ? moment(miniCalendarCurrentDate).format("MMMM, YYYY")
    : moment().format("MMMM, YYYY");

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      <PageWrap>
        {/* Row 1 — Calendar title + tab group */}
        <TopBar>
          <TitleRow>
            <CalTitle>Calendar</CalTitle>
          </TitleRow>
          {tabBarExtraContent?.right && <div>{tabBarExtraContent.right}</div>}
        </TopBar>

        {/* Row 2 — Custom date navigation + view toggle */}
        <NavBar>
          <DateNavGroup>
            <DateRangeText>{viewTitle}</DateRangeText>
            <NavBtn onClick={handlePrev} aria-label="Previous"><ChevronLeft size={9} /></NavBtn>
            <NavBtn onClick={handleNext} aria-label="Next"><ChevronRight size={9} /></NavBtn>
          </DateNavGroup>
          <ViewGroup>
            <ViewBtn $active={currentView === "timeGridDay"} $notlast onClick={() => handleViewChange("timeGridDay")}>
              Day
            </ViewBtn>
            <ViewBtn $active={currentView === "timeGridWeek"} onClick={() => handleViewChange("timeGridWeek")}>
              Week
            </ViewBtn>
          </ViewGroup>
        </NavBar>

        {/* Row 3 — Sidebar + Calendar */}
        <MainContent>
          {/* Left sidebar */}
          <Sidebar>
            <MiniCalHead>
              <span>{miniMonth}</span>
              <MiniNavGroup>
                <MiniNavBtn onClick={previousMonth}><ChevronLeft size={8} /></MiniNavBtn>
                <MiniNavBtn onClick={nextMonth}><ChevronRight size={8} /></MiniNavBtn>
              </MiniNavGroup>
            </MiniCalHead>

            <MiniMonth date={miniCalendarCurrentDate} onPick={handleMiniPick} />

            {/* Filter toggles */}
            <FilterSection>
              {[
                { key: "matter", label: "Matter", color: "#10B981" },
                { key: "event", label: "Event", color: "#F59E0B" },
                { key: "leave", label: "Leave", color: "#E11D48" },
                { key: "holiday", label: "Holiday", color: "#ff0000" },
                { key: "outlook", label: "Outlook", color: "#3B82F6" },
              ].map(({ key, label, color }) => (
                <button
                  key={key}
                  onClick={() => toggle(key)}
                  style={{
                    display: "flex", alignItems: "center", gap: 10,
                    background: "none", border: "none", cursor: "pointer",
                    padding: 0, opacity: show[key] ? 1 : 0.45,
                    transition: "opacity 0.15s",
                  }}
                >
                  <span style={{
                    width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                    background: show[key] ? color : "#d9d9d9",
                    border: `1.875px solid ${show[key] ? color : "#d9d9d9"}`,
                    boxSizing: "border-box",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "background 0.15s",
                  }}>
                    {show[key] && (
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                        <path d="M2 6L5 9L10 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </span>
                  <span style={{ fontSize: 16, color: "#636980", fontWeight: 400, userSelect: "none" }}>{label}</span>
                </button>
              ))}
            </FilterSection>

            {/* Outlook connect / sync */}
            <div style={{ marginTop: 16 }}>
              {outlookConnected ? (
                <button
                  onClick={() => fetchOutlookEvents(dateRange.start, dateRange.end)}
                  style={{
                    width: "100%", padding: "7px 10px", fontSize: 13,
                    background: "#EFF6FF", color: "#0078d4",
                    border: "1.5px solid #0078d4", borderRadius: 6,
                    cursor: "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", gap: 6, fontWeight: 500,
                  }}
                >
                  <SyncIcon /> Sync Outlook
                </button>
              ) : (
                <button
                  onClick={handleConnectOutlook}
                  style={{
                    width: "100%", padding: "7px 10px", fontSize: 13,
                    background: "#0078d4", color: "#fff",
                    border: "none", borderRadius: 6,
                    cursor: "pointer", display: "flex", alignItems: "center",
                    justifyContent: "center", gap: 6, fontWeight: 500,
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                    <rect width="9" height="9" fill="#F25022" /><rect x="11" width="9" height="9" fill="#7FBA00" />
                    <rect y="11" width="9" height="9" fill="#00A4EF" /><rect x="11" y="11" width="9" height="9" fill="#FFB900" />
                  </svg>
                  Connect Outlook
                </button>
              )}
            </div>
          </Sidebar>

          {/* Main calendar */}
          <CalArea>
            <FcWrap>
              <Spin spinning={plannerLoading}>
                <CalendarContainer>
                  <FullCalendar
                    ref={calendarRef}
                    plugins={[timeGridPlugin, dayGridPlugin, interactionPlugin]}
                    initialView="timeGridWeek"
                    height="100%"
                    stickyHeaderDates={true}
                    events={fcEvents}
                    headerToolbar={false}
                    datesSet={handleDatesSet}
                    eventClick={handleEventClick}
                    moreLinkClick={handleMoreLinkClick}
                    moreLinkAction="click"
                    moreLinkContent={(args) => (
                      <span style={{
                        display: "inline-block", background: "#1f2937", color: "#fff",
                        fontSize: 12, fontWeight: 600, padding: "3px 10px",
                        borderRadius: 20, whiteSpace: "nowrap", marginTop: 100,
                      }}>
                        +{args.num} more
                      </span>
                    )}
                    editable={false}
                    selectable={false}
                    allDaySlot={true}
                    allDayText="All Day"
                    nowIndicator={true}
                    slotMinTime="00:00:00"
                    slotMaxTime="24:00:00"
                    scrollTime="08:00:00"
                    slotDuration="01:00:00"
                    slotLabelFormat={{ hour: "numeric", minute: "2-digit", hour12: true }}
                    dayHeaderContent={(arg) => {
                      const d = moment(arg.date);
                      return (
                        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                          <span style={{ fontSize: 16, fontWeight: 500, color: "#636980", letterSpacing: "-0.64px" }}>
                            {d.format("ddd")}
                          </span>
                          <span style={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            width: 37, height: 26,
                            fontSize: 18, fontWeight: 600, letterSpacing: "-0.64px",
                            color: arg.isToday ? "#fff" : "#1f243b",
                            background: arg.isToday ? "#1f243b" : "transparent",
                            borderRadius: 3,
                          }}>
                            {d.format("D")}
                          </span>
                        </div>
                      );
                    }}
                    eventOverlap={true}
                    slotEventOverlap={true}
                    dayMaxEventRows={false}
                    dayMaxEvents={false}
                    eventMaxStack={4}
                    eventOrder="order,start,-duration,allDay,title"
                    eventDidMount={(info) => {
                      const borderColor = info.event.borderColor;
                      const bgColor = info.event.backgroundColor;
                      const outerEl = info.el.closest(".fc-daygrid-event, .fc-timegrid-event") || info.el.parentElement;
                      if (outerEl && borderColor) {
                        outerEl.style.border = "none";
                        outerEl.style.borderLeft = `3px solid ${borderColor}`;
                        outerEl.style.borderRadius = "6px";
                        outerEl.style.background = bgColor || "";
                        outerEl.style.overflow = "hidden";
                        outerEl.dataset.borderColor = borderColor;
                        outerEl.dataset.bgColor = bgColor || "";
                      }
                      if (!info.event.allDay) return;
                      setTimeout(() => {
                        const harness = info.el.closest(".fc-daygrid-event-harness");
                        const eventsContainer = harness?.parentElement;
                        const frame = eventsContainer?.parentElement;
                        if (!eventsContainer || !frame) return;
                        const frameH = frame.offsetHeight || 50;
                        const siblings = eventsContainer.querySelectorAll(".fc-daygrid-event-harness");
                        const count = siblings.length || 1;
                        const itemH = Math.floor((frameH - (count - 1)) / count);
                        siblings.forEach((h) => {
                          h.style.position = "relative";
                          h.style.top = "0";
                          h.style.inset = "unset";
                          h.style.height = `${itemH}px`;
                          h.style.minHeight = `${itemH}px`;
                          const ev = h.querySelector(".fc-daygrid-event");
                          if (ev) {
                            ev.style.height = "100%";
                            ev.style.minHeight = "0";
                            ev.style.margin = "0";
                            ev.style.position = "relative";
                            ev.style.top = "0";
                            const bc = ev.dataset.borderColor || borderColor;
                            const bg = ev.dataset.bgColor || bgColor;
                            if (bc) {
                              ev.style.border = "none";
                              ev.style.borderLeft = `3px solid ${bc}`;
                              ev.style.borderRadius = "6px";
                              ev.style.background = bg || "";
                              ev.style.overflow = "hidden";
                            }
                          }
                        });
                        eventsContainer.style.display = "flex";
                        eventsContainer.style.flexDirection = "column";
                        eventsContainer.style.gap = "1px";
                        eventsContainer.style.height = `${frameH}px`;
                        eventsContainer.style.margin = "0";
                        eventsContainer.style.padding = "0";
                      }, 0);
                    }}
                    eventContent={(info) => {
                      const color = info.event.textColor || "#333";
                      const isHoliday = info.event.extendedProps?._type === "holiday";
                      const durationMins =
                        info.event.start && info.event.end
                          ? moment(info.event.end).diff(moment(info.event.start), "minutes")
                          : 0;
                      const isCompact = durationMins < 60;
                      const isLeave = info.event.extendedProps?._type === "leave";
                      const inner = (
                        <div
                          title={info.event.title}
                          style={{
                            padding: "3px 6px", background: "transparent", color,
                            height: "100%", width: "100%", boxSizing: "border-box",
                            overflow: "hidden", display: "flex", flexDirection: "column",
                            gap: 2, cursor: "pointer", position: "relative",
                          }}
                        >
                          <span style={{ fontWeight: 600, fontSize: 14, lineHeight: "18px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                            {info.event.title}
                          </span>
                          {isHoliday && (
                            <span
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                background: "#ff0000",
                                color: "#ffffff",
                                fontSize: 12,
                                lineHeight: 1,
                                fontWeight: 700,
                                boxSizing: "border-box",
                                overflow: "hidden",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                flexShrink: 0,
                              }}
                            >
                              H
                            </span>
                          )}
                          {!isCompact && info.event.extendedProps?._type === "event" && info.event.start && info.event.end && (
                            <span style={{ fontSize: 12, opacity: 0.85, whiteSpace: "nowrap" }}>
                              {moment(info.event.start).format("hh:mm A")} – {moment(info.event.end).format("hh:mm A")}
                            </span>
                          )}
                          {info.event.extendedProps?._type === "leave" && (
                            <span style={{ fontSize: 12, opacity: 0.85, whiteSpace: "nowrap" }}>
                              08:00 AM – 05:00 PM
                            </span>
                          )}
                          {!isCompact && info.event.extendedProps?._type === "matter" && (
                            <>
                              {info.event.start && info.event.end && (
                                <span style={{ fontSize: 12, opacity: 0.85, whiteSpace: "nowrap" }}>
                                  {moment(info.event.start).format("hh:mm A")} – {moment(info.event.end).format("hh:mm A")}
                                </span>
                              )}
                              {info.event.extendedProps?._dailyHours != null && (
                                <span style={{ fontSize: 12, fontWeight: 600, opacity: 0.9, whiteSpace: "nowrap" }}>
                                  {formatHours(info.event.extendedProps._dailyHours)}
                                </span>
                              )}
                            </>
                          )}
                        </div>
                      );

                      if (isLeave) {
                        return (
                          <StaffLeaveIndicatorInCalendar
                            leaveDetails={info.event.extendedProps._raw}
                            isStaff={true}
                            reloadList={fetchPlannerData}
                            openEditLeaveModal={(details) => setLeaveForm({ open: true, data: details })}
                            EditLeaveModal={(details) => setLeaveForm({ open: true, data: details })}
                          >
                            {inner}
                          </StaffLeaveIndicatorInCalendar>
                        );
                      }

                      return inner;
                    }}
                  />
                </CalendarContainer>
              </Spin>
            </FcWrap>
          </CalArea>
        </MainContent>
      </PageWrap>

      {/* ── Detail modal ───────────────────────────────────────────────────── */}
      <Modal
        open={modal.open}
        onClose={closeModal}
        width={620}
        title={
          modal.type === "event" ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: "100%",
              }}
            >
              <span style={{ fontSize: 17, fontWeight: 700, color: "#111" }}>Event Details</span>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                {modal.data?.createdby === userInfo?.userid && eventMinePermission?.edit && (
                  <EditOutlined onClick={handleEditClick} style={ICON_BTN_STYLE} />
                )}
                {modal.data?.createdby === userInfo?.userid && eventMinePermission?.delete && (
                  <DeleteOutlined onClick={handleDeleteClick} style={ICON_BTN_STYLE} />
                )}
              </div>
            </div>
          ) : (
            <span style={{ fontSize: 16, fontWeight: 700, color: "#111" }}>
              {modal.type === "matter" && "Matter Details"}
              {modal.type === "holiday" && "Holiday Details"}
            </span>
          )
        }
      >
        {modal.loading ? (
          <div style={{ display: "flex", justifyContent: "center", padding: "40px 0" }}>
            <Spin size="large" />
          </div>
        ) : modal.data && (
          <div style={{ paddingTop: 4 }}>
            {modal.type === "event" && (
              <>
                {modal.data.createdby && (
                  <div style={{
                    fontSize: 12, color: "#888", background: "#fafafa",
                    borderRadius: 6, padding: "6px 12px", marginBottom: 12, borderBottom: "1px solid #f0f0f0",
                  }}>
                    <b>Created by:</b>{" "}
                    {resolveNames([modal.data.createdby])}
                    {modal.data.createdat ? "; " + moment(modal.data.createdat).format("Do MMM, YYYY") : ""}
                  </div>
                )}
                <EvDetailRow><EvDetailLabel>Event Name</EvDetailLabel><EvDetailValue>{modal.data.eventname || "---"}</EvDetailValue></EvDetailRow>
                <EvDetailRow><EvDetailLabel>Matter</EvDetailLabel><EvDetailValue>{modal.data.mattername || "---"}</EvDetailValue></EvDetailRow>
                <EvDetailRow><EvDetailLabel>Start Date &amp; Time</EvDetailLabel><EvDetailValue>{formatDateTime(modal.data.startdatetime || modal.data.start_datetime)}</EvDetailValue></EvDetailRow>
                <EvDetailRow><EvDetailLabel>End Date &amp; Time</EvDetailLabel><EvDetailValue>{formatDateTime(modal.data.enddatetime || modal.data.end_datetime)}</EvDetailValue></EvDetailRow>
                <EvDetailRow><EvDetailLabel>Location</EvDetailLabel><EvDetailValue>{modal.data.location || "---"}</EvDetailValue></EvDetailRow>
                <EvDetailRow className="section-title">
                  <EvDetailLabel>Meeting Link</EvDetailLabel>
                  <EvDetailValue>
                    {modal.data.meetinglink
                      ? <a href={modal.data.meetinglink} target="_blank" rel="noreferrer">{modal.data.meetinglink}</a>
                      : "---"}
                  </EvDetailValue>
                </EvDetailRow>
                <EvDetailRow><EvDetailLabel><b>Attendees</b></EvDetailLabel><EvDetailValue /></EvDetailRow>
                <EvDetailRow>
                  <EvDetailLabel>Client</EvDetailLabel>
                  <EvDetailValue>
                    {Array.isArray(modal.data.clientnames) && modal.data.clientnames.length
                      ? modal.data.clientnames.join(", ")
                      : modal.data.clientpersonname || "---"}
                  </EvDetailValue>
                </EvDetailRow>
                <EvDetailRow>
                  <EvDetailLabel>Responsible Personnel</EvDetailLabel>
                  <EvDetailValue>
                    {Array.isArray(modal.data.responsiblepersonelname) && modal.data.responsiblepersonelname.length
                      ? modal.data.responsiblepersonelname.join(", ")
                      : modal.data.responsiblepersonelname || "---"}
                  </EvDetailValue>
                </EvDetailRow>
                <EvDetailRow className="section-title">
                  <EvDetailLabel>Employees</EvDetailLabel>
                  <EvDetailValue>{resolveNames(modal.data.attendees)}</EvDetailValue>
                </EvDetailRow>
                {modal.data.description && (
                  <EvDetailRow style={{ flexDirection: "column", gap: 4 }}>
                    <EvDetailLabel><b>Description</b></EvDetailLabel>
                    <EvDetailValue style={{ fontSize: 13, color: "#555", lineHeight: 1.7 }}>{modal.data.description}</EvDetailValue>
                  </EvDetailRow>
                )}
                <EvDetailRow>
                  <EvDetailLabel>Reminder</EvDetailLabel>
                  {modal.data.sendremaindercount?.length > 0 ? (
                    <EvDetailValue>
                      {(() => {
                        const count = Number(modal.data.sendremindercount || modal.data.sendremaindercount || 0);
                        const type = modal.data.sendremindertype || modal.data.sendremaindertype || "";
                        const singularMap = { minutes: "Minute", hours: "Hour", days: "Day", weeks: "Week" };
                        const label = count === 1
                          ? singularMap[type?.toLowerCase()] || type
                          : (singularMap[type?.toLowerCase()] ? singularMap[type?.toLowerCase()] + "s" : type);
                        return `Before ${count} ${label}`;
                      })()}
                    </EvDetailValue>
                  ) : <EvDetailValue>-</EvDetailValue>}
                </EvDetailRow>
              </>
            )}
            {modal.type === "matter" && (() => {
              const personDay = parseFloat(modal.data.personDay) || 0;
              const start = moment(modal.data.actualBookedStartDate, "YYYY-MM-DD");
              const end = moment(modal.data.actualBookedEndDate, "YYYY-MM-DD");
              const bookedWorkingDays = parseInt(modal.data.bookedWorkingDays) || 0;
              const dailyHours = (personDay > 0 && bookedWorkingDays > 0)
                ? Math.min((personDay * 8) / bookedWorkingDays, 8).toFixed(2) : null;
              return (
                <>
                  <DetailRow label="Matter Name" value={modal.data.matterName} />
                  <DetailRow label="Start Date" value={start.isValid() ? start.format("Do MMM YYYY") : null} />
                  <DetailRow label="End Date" value={end.isValid() ? end.format("Do MMM YYYY") : null} />
                  <DetailRow label="Booked Days" value={bookedWorkingDays > 0 ? `${bookedWorkingDays} days` : null} />
                  <DetailRow label="Mandays" value={personDay > 0 ? `${personDay} days` : null} />
                  <DetailRow label="Hrs \ Day" value={dailyHours ? formatHours(dailyHours) : null} />
                </>
              );
            })()}
            {modal.type === "holiday" && (() => {
              const start = moment(modal.data.start_date, "YYYY-MM-DD");
              const end = moment(modal.data.end_date, "YYYY-MM-DD");
              return (
                <>
                  <DetailRow label="Holiday Name" value={modal.data.holiday_name} />
                  <DetailRow label="Start Date" value={start.isValid() ? start.format("Do MMM YYYY") : null} />
                  <DetailRow label="End Date" value={end.isValid() && end.isSameOrAfter(start, "day") ? end.format("Do MMM YYYY") : null} />
                </>
              );
            })()}
          </div>
        )}
      </Modal>

      {/* ── All Bookings modal ─────────────────────────────────────────────── */}
      <Modal
        open={allBookings.open}
        onClose={closeAllBookings}
        width={520}
        title={<span style={{ fontSize: 15, fontWeight: 700, color: "#111" }}>All Bookings — {allBookings.date}</span>}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 10, maxHeight: 480, overflowY: "auto" }}>
          {allBookings.items.map((item) => {
            const type = item.extendedProps?._type;
            const raw = item.extendedProps?._raw;
            const dailyHours = item.extendedProps?._dailyHours;
            // use the SAME colors as the calendar event blocks
            const bg = item.backgroundColor || "#eee";
            const border = item.borderColor || "#ccc";
            const text = item.textColor || "#1F243B";
            const label = { event: "Event", leave: "Leave", matter: "Matter", outlook: "Outlook", holiday: "Holiday" }[type] || type;
            return (
              <div
                key={item.id}
                onClick={() => {
                  closeAllBookings();
                  if (type === "leave") {
                    setLeaveForm({ open: true, data: raw });
                  } else if (type === "matter" || type === "holiday") {
                    setModal({ open: true, type, data: raw, loading: false });
                  } else if (type === "event") {
                    deleteEventIdRef.current = item.extendedProps._id;
                    setModal({ open: true, type: "event", data: null, loading: true });
                    CALL_FEATCH_API("insyts-calendar/getallevents", "post", { eventid: item.extendedProps._id })
                      .then((res) => {
                        const d = res?.response?.data ?? res?.data;
                        setModal({ open: true, type: "event", data: Array.isArray(d) ? d[0] : d, loading: false });
                      })
                      .catch(() => { showError("Failed to load event details"); closeModal(); });
                  }
                }}
                style={{
                  background: bg,
                  borderLeft: `3px solid ${border}`,
                  borderRadius: 6,
                  padding: "12px 16px",
                  cursor: "pointer",
                  color: text,
                }}
              >
                <div style={{ fontWeight: 600, fontSize: 14, lineHeight: "18px" }}>{item.title}</div>
                {item.start && item.end && (
                  <div style={{ fontSize: 12, marginTop: 3, opacity: 0.85 }}>
                    {moment(item.start).format("hh:mm A")} – {moment(item.end).format("hh:mm A")}
                  </div>
                )}
                {type === "matter" && dailyHours != null && (
                  <div style={{ fontSize: 12, marginTop: 3, opacity: 0.9, fontWeight: 600 }}>
                    {formatHours(dailyHours)}
                  </div>
                )}
                <div style={{
                  display: "inline-block", marginTop: 8,
                  background: border, color: "#fff", borderRadius: 4,
                  padding: "2px 8px", fontSize: 11, fontWeight: 600,
                }}>
                  {label}
                </div>
              </div>
            );
          })}
        </div>
      </Modal>

      {/* ── Outlook Event Detail Modal ──────────────────────────────────────── */}
      <Modal
        open={showOutlookModal}
        onClose={() => { setShowOutlookModal(false); setSelectedOutlookData(null); }}
        width={480}
        title={<span style={{ fontSize: 17, fontWeight: 700, color: "#111" }}>Outlook Event Details</span>}
      >
        {selectedOutlookData && (
          <div style={{ paddingTop: 4 }}>
            {[
              ["Subject", selectedOutlookData.subject],
              ["Start", selectedOutlookData.start?.dateTime ? moment(selectedOutlookData.start.dateTime).format("DD MMM YYYY hh:mm A") : null],
              ["End", selectedOutlookData.end?.dateTime ? moment(selectedOutlookData.end.dateTime).format("DD MMM YYYY hh:mm A") : null],
              ["Location", selectedOutlookData.location?.displayName || "-"],
            ].map(([label, value]) => (
              <div key={label} style={{ display: "flex", padding: "10px 0", borderBottom: "1px solid #f0f0f0" }}>
                <span style={{ width: 140, flexShrink: 0, color: "#888", fontSize: 13 }}>{label}</span>
                <span style={{ color: "#111", fontSize: 13.5, fontWeight: 450 }}>{value || "---"}</span>
              </div>
            ))}
          </div>
        )}
      </Modal>

      {/* ── Leave Form (same modal as Leave calendar) ───────────────────────── */}
      <LeaveForm
        isModalVisible={leaveForm.open}
        closeModal={() => setLeaveForm({ open: false, data: {} })}
        leaveDetails={leaveForm.data}
        getLeaveList={fetchPlannerData}
      />

      {/* ── Edit Event Modal ─────────────────────────────────────────────────── */}
      {editVisible && modal.data && (
        <CreateEventModal
          visible={editVisible}
          onClose={(shouldRefresh) => {
            setEditVisible(false);
            if (shouldRefresh) fetchPlannerData();
          }}
          editData={buildEditData(modal.data, userInfo?.userid || "")}
          isEditMode={true}
        />
      )}

      {/* ── Delete Confirmation Modal ────────────────────────────────────────── */}
      <Modal
        open={deleteConfirmOpen}
        onClose={() => { setDeleteConfirmOpen(false); setSendCancellationEmail(false); }}
        width={500}
        title={<span style={{ fontSize: 16, fontWeight: 700, color: "#111827", textAlign: "center", display: "block" }}>Delete Event</span>}
      >
        <div style={{ padding: "8px 0" }}>
          <p style={{ marginBottom: 20, fontSize: 14, color: "#111827" }}>
            Are you sure you want to delete this event?
          </p>
          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px", marginBottom: 28 }}>
            <StyledCheckbox
              checked={sendCancellationEmail}
              onChange={(e) => setSendCancellationEmail(e.target.checked)}
            >
              <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500 }}>
                Please send a cancellation email to all attendees
              </span>
            </StyledCheckbox>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 16 }}>
            <CancelConfirmBtn onClick={() => { setDeleteConfirmOpen(false); setSendCancellationEmail(false); }} style={{ width: 150, borderRadius: 10 }}>
              No
            </CancelConfirmBtn>
            <DeleteBtn onClick={handleDeleteConfirm} loading={deleteLoading} style={{ width: 150 }}>
              Yes
            </DeleteBtn>
          </div>
        </div>
      </Modal>

      {/* ── Toasts ──────────────────────────────────────────────────────────── */}
      {toasts.length > 0 && createPortal(
        <ToastWrap>
          {toasts.map((t) => (
            <ToastItem key={t.id}>&#9888; {t.msg}</ToastItem>
          ))}
        </ToastWrap>,
        document.body
      )}
    </>
  );
}

// ─── Outlook event builder ────────────────────────────────────────────────────

function buildOutlookItems(rawItems) {
  return (rawItems ?? []).map((item) => ({
    id: `outlook-${item.id}`,
    title: item.subject || "Outlook Event",
    start: item.isAllDay
      ? moment(item.start.dateTime).format("YYYY-MM-DD")
      : moment(item.start.dateTime).format("YYYY-MM-DDTHH:mm:ss"),
    end: item.isAllDay
      ? moment(item.end.dateTime).add(1, "day").format("YYYY-MM-DD")
      : moment(item.end.dateTime).format("YYYY-MM-DDTHH:mm:ss"),
    allDay: !!item.isAllDay,
    backgroundColor: "#D5E3FC",
    borderColor: "#3B82F6",
    textColor: "#1E40AF",
    extendedProps: { _type: "outlook", ...item },
  }));
}

const mapStateToProps = (state) => ({
  userInfo: getUserInfo(state),
  ClientId: getClientid(state),
  userList: getUserList(state),
});

export default connect(mapStateToProps, null)(MyPlannerCalendar);